import { useMutation as useRQMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import MutationKeys from 'hooks/mutations/keys';
import QueryKeys from 'hooks/queries/keys';
import useGetAxiosInstance from 'hooks/useGetAxiosInstance';
import { ApiMethods, CuredApiPaths } from 'models/enums';

import type { CreateSavedRowResponse } from 'hooks/mutations/useCreateSavedRow';

export type UpdateSavedRowInput = {
    id: string;
    name: string;
    category: string;
    row: Record<string, unknown>;
    /** Category slug before this update (for cache invalidation when it changes). */
    previousCategory: string;
};

/** PUT /saved-rows/:id — update library row metadata + JSON. */
const useUpdateSavedRow = (mutateOptions?: UseMutationOptions<CreateSavedRowResponse, Error, UpdateSavedRowInput>) => {
    const queryClient = useQueryClient();
    const getApiInstance = useGetAxiosInstance(ApiMethods.PUT);
    const { onSuccess: onSuccessOption, ...restMutateOptions } = mutateOptions ?? {};

    return useRQMutation<CreateSavedRowResponse, Error, UpdateSavedRowInput>({
        mutationKey: MutationKeys.UPDATE_SAVED_ROW,
        mutationFn: async (variables) => {
            const api = await getApiInstance();
            const { data } = await api.put<CreateSavedRowResponse>(`${CuredApiPaths.SAVED_ROWS}/${variables.id}`, {
                name: variables.name,
                category: variables.category,
                row: variables.row,
            });
            return data;
        },
        ...restMutateOptions,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries(QueryKeys.SAVED_ROWS_CATEGORY(variables.category));
            queryClient.invalidateQueries(QueryKeys.SAVED_ROWS_CATEGORY(variables.previousCategory));
            queryClient.invalidateQueries({ queryKey: QueryKeys.SAVED_ROWS_CATEGORY_LIST });
            onSuccessOption?.(data, variables, context);
        },
    });
};

export default useUpdateSavedRow;
